import { computed, ref } from 'vue'

import { conversionApi, type ConversionApi } from '../api/ConversionApi'
import { SaveKeyEventsFlow } from '../flows/SaveKeyEventsFlow'
import type {
  KeyEventAccount,
  KeyEventDescriptor,
  KeyEventSelection,
} from '../models/conversion.types'
import {
  accountIdentity,
  accountLabel,
  liveEventsForAccount,
  selectionForAccount,
} from '../service/keyEvents'

import { useAccount, useWorkspace } from '@/features/account'
import { useIntegrations } from '@/features/integrations'
import { useWorkspaceAttribute } from '@/features/workspace-settings'

export function useKeyEvents(
  descriptor: KeyEventDescriptor,
  api: ConversionApi = conversionApi,
  flow = new SaveKeyEventsFlow(),
) {
  const accountScope = useAccount()
  const workspace = useWorkspace()
  const integrations = useIntegrations()
  const events = useWorkspaceAttribute(descriptor.eventsKey)
  const primary = useWorkspaceAttribute(descriptor.primaryKey)
  const ga4Set = useWorkspaceAttribute('ga4_key_events_set')
  const liveLoading = ref(false)
  const pending = ref(false)
  const error = ref<unknown>(null)

  const accounts = computed<KeyEventAccount[]>(() =>
    integrations.accountsFor(descriptor.channel).map((account) => {
      const accountId = accountIdentity(account, descriptor)
      return {
        account,
        accountId,
        label: accountLabel(account),
        selection: selectionForAccount(accountId, events.value.value, primary.value.value),
      }
    }),
  )

  async function fetchLive(accountId: string): Promise<string[] | null> {
    const accountIdScope = accountScope.accountId.value
    const workspaceId = workspace.workspaceId.value
    if (accountIdScope === undefined || workspaceId === undefined) return null
    liveLoading.value = true
    error.value = null
    try {
      const payload =
        descriptor.platform === 'ga4'
          ? await api.getGa4KeyEvents(accountIdScope, workspaceId)
          : await api.getMmpKeyEvents(accountIdScope, workspaceId, descriptor.platform)
      return liveEventsForAccount(payload, accountId)
    } catch (cause) {
      error.value = cause
      return null
    } finally {
      liveLoading.value = false
    }
  }

  async function save(accountId: string, selection: KeyEventSelection): Promise<boolean> {
    pending.value = true
    error.value = null
    try {
      await flow.save({
        descriptor,
        accountId,
        selection,
        current: {
          events: events.value.value,
          primary: primary.value.value,
        },
        ids: {
          events: events.id.value,
          primary: primary.id.value,
          ...(descriptor.platform === 'ga4' ? { ga4Set: ga4Set.id.value } : {}),
        },
      })
      return true
    } catch (cause) {
      error.value = cause
      return false
    } finally {
      pending.value = false
    }
  }

  return { descriptor, accounts, liveLoading, pending, error, fetchLive, save }
}

export type UseKeyEvents = ReturnType<typeof useKeyEvents>
